// ─────────────────────────────────────────────────────────────────────────────────────────────
// COPIE VERSIONNÉE — NE PAS MODIFIER LES CORRÉLATIONS ICI SANS METTRE À JOUR L'ORIGINE.
// Origine   : dépôt VLT Devis, public/outils/metiers/cvc/psychro.mjs
//             (C:\Users\wecha\vlt-devis-saas\public\outils\metiers\cvc\psychro.mjs)
// Version   : commit 53f27dce35157919e1bdb92bf9dcd6c1e135e83c du 18/09/2026, fichier du 18/09/2026 17:30
// Empreinte : SHA-256 du source 1547C89CB7671D62EFC0A55E306345B029693439C3ED45151ADCB834EE9309F0
// Copie     : 07/10/2026, portage TypeScript TEL QUEL (annotations de type seulement, aucun coefficient changé).
// Tests     : tests/unit/cvc-moteur.test.ts (tests 1 à 3 de l'origine, mêmes valeurs).
// ─────────────────────────────────────────────────────────────────────────────────────────────
// Assistant Climatisation CVC — psychrométrie (air humide), moteur PUR.
//
// Portage à l'identique des corrélations du calculateur CVC 1.0.0 (« Facture Eco CVC »,
// dist/engine.mjs + METHOD.md) : corrélations ASHRAE Fundamentals 2017 réimplémentées.
// Aucun code de bibliothèque tierce n'est embarqué.
// Test de référence conservé : 25 °C / 80 % HR → point de rosée 21,309397 °C.
//
// Unités : température °C, pression Pa, humidité spécifique w en kg d'eau / kg d'air sec,
// enthalpie h en kJ/kg d'air sec, volume spécifique v en m³/kg d'air sec.
// Pur : aucun DOM, aucun réseau, aucun hasard. Même entrée → même sortie.

export const P_ATM = 101325;

export type ModeHygro = 'hr' | 'humide' | 'rosee' | 'w' | 'h';

export interface EtatAir {
  t: number; hr: number; w: number; h: number; rosee: number | null; humide: number | null; v: number;
}

export function borne(x: unknown, min: number, max: number, libelle: string): number {
  if (typeof x !== 'number' || !Number.isFinite(x) || x < min || x > max) {
    throw new Error(`${libelle} : valeur attendue entre ${min} et ${max}.`);
  }
  return x;
}

/** Pression de vapeur saturante (Pa). Sur glace jusqu'à 0,01 °C, sur eau au-dessus. */
export function psat(t: number): number {
  borne(t, -60, 80, 'Température');
  const T = t + 273.15;
  return Math.exp(t <= 0.01
    ? -5674.5359 / T + 6.3925247 - 0.009677843 * T + 0.00000062215701 * T * T
      + 2.0747825e-9 * T ** 3 - 9.484024e-13 * T ** 4 + 4.1635019 * Math.log(T)
    : -5800.2206 / T + 1.3914993 - 0.048640239 * T + 0.000041764768 * T * T
      - 1.4452093e-8 * T ** 3 + 6.5459673 * Math.log(T));
}

/** Humidité spécifique (kg/kg sec) à partir de l'humidité relative en %. */
export function humiditeAbsolue(t: number, hr: number, p: number = P_ATM): number {
  borne(hr, 0, 100, 'Humidité relative');
  borne(p, 60000, 110000, 'Pression');
  const pv = psat(t) * hr / 100;
  if (pv >= p) throw new Error('État psychrométrique impossible.');
  return 0.621945 * pv / (p - pv);
}

/** Enthalpie de l'air humide (kJ/kg sec). */
export const enthalpie = (t: number, w: number): number => 1.006 * t + w * (2501 + 1.86 * t);

/** Inversion monotone par bissection (70 itérations : convergence à 1e-18 près du domaine). */
function bissection(f: (x: number) => number, cible: number, bas: number, haut: number): number {
  for (let i = 0; i < 70; i++) {
    const m = (bas + haut) / 2;
    if (f(m) > cible) haut = m; else bas = m;
  }
  return (bas + haut) / 2;
}

/** Humidité spécifique déduite de la température humide (saturation adiabatique). */
export function humiditeDepuisHumide(t: number, th: number, p: number = P_ATM): number {
  borne(th, 0, t, 'Température humide');
  const ws = humiditeAbsolue(th, 100, p);
  return ((2501 - 2.326 * th) * ws - 1.006 * (t - th)) / (2501 + 1.86 * t - 4.186 * th);
}

/**
 * État complet de l'air.
 * mode : 'hr' (% HR) · 'humide' (°C bulbe humide) · 'rosee' (°C) · 'w' (g/kg sec) · 'h' (kJ/kg sec).
 */
export function etatAir(t: number, valeur: number, mode: ModeHygro = 'hr', p: number = P_ATM): EtatAir {
  borne(t, -30, 60, 'Température sèche');
  let w: number;
  if (mode === 'hr') w = humiditeAbsolue(t, valeur, p);
  else if (mode === 'humide') w = humiditeDepuisHumide(t, valeur, p);
  else if (mode === 'rosee') { borne(valeur, -60, t, 'Point de rosée'); w = humiditeAbsolue(valeur, 100, p); }
  else if (mode === 'w') w = valeur / 1000;
  else if (mode === 'h') w = (valeur - 1.006 * t) / (2501 + 1.86 * t);
  else throw new Error('Mode hygrométrique inconnu.');
  borne(w, 0, humiditeAbsolue(t, 100, p), 'Humidité spécifique');
  const pv = p * w / (0.621945 + w);
  const hr = 100 * pv / psat(t);
  const rosee = w === 0 ? null : bissection(psat, pv, -60, t);
  // Sous 0 °C la saturation adiabatique n'est plus inversible ici : on ne fabrique pas de valeur.
  const humide = t <= 0 || humiditeDepuisHumide(t, 0, p) > w ? null : bissection((x) => humiditeDepuisHumide(t, x, p), w, 0, t);
  return { t, hr, w, h: enthalpie(t, w), rosee, humide, v: 287.042 * (t + 273.15) * (1 + 1.607858 * w) / p };
}
